import { useEffect, useRef, useState } from "react";
import { Box, InputLabel } from "@mui/material";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import {
  AccountMember,
  searchAccountMembers,
} from "screens/Settings/Workspaces/WorkspaceMembersServices";
import styles from "./AccountMemberPicker.styles";

interface AccountMemberPickerProps {
  id: string;
  label: string;
  placeholder?: string;
  /** Current email string — controlled by the parent. */
  value: string;
  /** Called with the typed or picked email. */
  onChange: (email: string) => void;
  /**
   * Called whenever validity changes — true only when `value` matches a real, searched account
   * member. Callers gate submit on this so an arbitrary/typed address can never be selected.
   */
  onValidityChange?: (isMember: boolean) => void;
}

/**
 * Shared type-ahead over account members (searchAccountMembers), used by the Share and Reassign
 * dialogs. Owns the search + debounce and reports validity; the parent owns the value string.
 */
const AccountMemberPicker = ({
  id,
  label,
  placeholder = "Search team members",
  value,
  onChange,
  onValidityChange,
}: AccountMemberPickerProps) => {
  const [options, setOptions] = useState<AccountMember[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<any>(null);
  // Monotonic request id: only the most recently issued search may apply its result, so a slow
  // earlier response can't overwrite a newer one (which would flip validity to a stale answer).
  const seqRef = useRef(0);

  const runSearch = async (text: string) => {
    const seq = ++seqRef.current;
    try {
      setLoading(true);
      // Cap generously so a typed exact-match recipient is still returned even when many members
      // share a common substring (a too-small cap can hide the intended member and wrongly mark
      // an exact email invalid, permanently disabling submit for that recipient).
      const r = await searchAccountMembers(text, 25);
      if (seq !== seqRef.current) return; // superseded by a newer search
      setOptions(Array.isArray(r) ? r : []);
    } catch {
      if (seq === seqRef.current) setOptions([]);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  };

  // Initial suggestions on mount; cancel any pending debounced search on unmount.
  useEffect(() => {
    runSearch("");
    return () => clearTimeout(timer.current);
  }, []);

  // Report validity whenever the value or the fetched options change (use a ref so an inline
  // onValidityChange doesn't re-fire the effect on every render).
  const isMember = options.some(
    (o) => o.email?.toLowerCase() === value.trim().toLowerCase(),
  );
  const notify = useRef(onValidityChange);
  notify.current = onValidityChange;
  useEffect(() => {
    notify.current?.(isMember);
  }, [isMember]);

  // Search for `text`: an emptied field restores the default list straight
  // away; anything typed waits for the debounce.
  const scheduleSearch = (text: string) => {
    clearTimeout(timer.current);
    if (!text) {
      runSearch("");
      return;
    }
    timer.current = setTimeout(() => runSearch(text), 400);
  };

  return (
    <Box>
      {/* Same label + gap `CustomInput` draws, so the picker lines up with the
          fields around it. `htmlFor` targets the autocomplete's input, which
          MUI gives this same `id`. */}
      <Box mb={formFieldMetrics.labelGap}>
        <InputLabel htmlFor={id} sx={customInputStyles.inputLabel}>
          {label}
        </InputLabel>
      </Box>
      <CustomAutoComplete
        id={id}
        placeHolder={placeholder}
        name={id}
        freeSolo={true}
        loading={loading}
        value={value}
        options={options}
        textFieldVariant="outlined"
        sx={styles.field}
        componentsProps={{ paper: { sx: styles.paper } }}
        filterOptions={(o: any) => o}
        getOptionLabel={(o: any) => (typeof o === "string" ? o : o.email || "")}
        onChange={(_e: any, v: any) => {
          const next =
            v && typeof v === "object"
              ? v.email || ""
              : typeof v === "string"
                ? v.trim()
                : "";
          onChange(next);
          // The clear (✕) button empties the value without a text-field change
          // event, so the default list has to be restored from here too.
          if (!next) scheduleSearch("");
        }}
        onChangeForTextField={(e: any) => {
          const val = e.target.value ?? "";
          onChange(val);
          scheduleSearch(val.trim());
        }}
        renderOption={(p: any, o: any) => (
          <li {...p} key={o.email}>
            <Box sx={styles.optionText}>
              <Box component="span" sx={styles.optionName}>
                {o.name}
              </Box>
              <Box component="span" sx={styles.optionEmail}>
                {o.email}
              </Box>
            </Box>
          </li>
        )}
      />
    </Box>
  );
};

export default AccountMemberPicker;
